/**
 * Validates `partJson` and `featuresJson` stored in the database for a part.
 * `ShapeSchema` is intentionally permissive (`.partial()`, nullable fields, loose enums) so legacy payloads still pass.
 *
 * WARNING: Adding strict validation rules may break existing parts at runtime.
 * Before tightening this schema, ensure every stored part JSON is compatible.
 * That is hard in practice: records span many formats (legacy payloads, partial CAD exports, etc.).
 *
 * To check compatibility against the full database:
 *   1. Build the API first (`pnpm build:api`) — the CLI runs from `dist/`
 *   2. Run `pnpm cli validate-parts`
 *
 * The script processes all parts in descending `createdAt` order and stops at the first failure.
 * As of today, every part from the present back to September 2025 validates successfully.
 */
import { z } from "zod";

export const ShapeFeatureSchema = z.enum([
  "Machining",
  "machining",
  "Folding",
  "Hole",
  "Tube",
  "Flat",
  "Side",
  "TubeStraight",
  "TubeFolded",
  "WholeTube",
  "Consumable",
  "consumable",
  "CONSUMABLE",
  "Interactions",
  "Profile",
]);

const ComplexitySchema = z.object({
  n_edges: z.number().nullish(),
  n_faces: z.number().nullish(),
  n_plane_faces: z.number().nullish(),
  n_torus_faces: z.number().nullish(),
  n_cylinder_faces: z.number().nullish(),
});

const DimensionsSchema = z
  .object({
    area: z.number().nullish(),
    bbxdx: z.number().nullish(),
    bbxdy: z.number().nullish(),
    bbxdz: z.number().nullish(),
    width: z.number().nullish(),
    length: z.number().nullish(),
    volume: z.number().nullish(),
    thickness: z.number().nullish(),
    diameter: z.number().nullish(),
    edge_length: z.number().nullish(),
    unfolded_width: z.number().nullish(),
    unfolded_length: z.number().nullish(),
    section_area: z.number().nullish(),
  })
  .optional();

const FeaturePropertiesSchema = z.object({
  area: z.number().nullish(),
  arc: z.number().nullish(),
  angle: z.number().nullish(),
  contact_length: z.number().nullish(),
  folding_length: z.number().nullish(),
  diameter: z.number().nullable().nullish(),
  hole_type: z
    .enum(["hole_circular", "hole_slotted", "hole_complex", "hole_simple", "hole_folded"])
    .nullish(),
  is_through: z.boolean().nullish(),
  edge_length: z.number().nullish(),
  has_chamfer: z.boolean().nullish(),
  length: z.number().nullish(),
  length_straight: z.number().nullish(),
  length_round: z.number().nullish(),
});

export const FeatureSchema = z.object({
  type: ShapeFeatureSchema,
  properties: FeaturePropertiesSchema,
});

export const ShapeSchema = z
  .object({
    group: z.number().nullable(),
    group_size: z.number().nullish(),
    __version__: z.string().optional(),
    shape_name: z.string().nullish(),
    shape_nb: z.number().nullish(),
    part_type: z
      .enum([
        "sheet",
        "consumable",
        "machining",
        "tube",
        "square_tube",
        "round_tube",
        "rectangular_tube",
        "square_bar",
        "round_bar",
        "profile",
        "assembly",
        "other",
        "billet",
      ])
      .nullish(),
    complexity: ComplexitySchema.nullable(),
    dimensions: DimensionsSchema,
    features: z.array(FeatureSchema).nullable(),
  })
  .partial();

export type Shape = z.infer<typeof ShapeSchema>;
export type ShapeFeatureType = z.infer<typeof ShapeFeatureSchema>;
